import { useGetLightCartQuery } from '@/redux/api/client/cart';
import classnames from 'classnames';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { ScreenSizeQueries } from '@/constants/breakpoints';
import { useCart } from '../hooks/useCart';
import { useCallback, useEffect, useRef } from 'react';
import CartItemsShimmer from './CartItemsShimmer';
import { MyCartLabels } from '../labels';
import CartItem from './CartItem';
import OrderSummaryPanel from '../OrderSummaryPanel';
import ContentArea from '@/components/cms/ContentArea';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import { PromoCodeInput } from '../PromoCodeInput';
import Button from '../../Buttons/Button';
import { trackSelectCheckoutEvent, trackViewCartEvent } from '@/analytics/ecommerce';
import { AnalyticsContext } from '@/analytics/constants';
import SomethingWentWrongMessage from './SomethingWentWrongMessage';

interface MyCartProps {
  context: 'panel' | 'page';
  onBusyStateChange?: (isBusy: boolean) => void;
}

const MyCart = (props: MyCartProps) => {
  const {
    cartData,
    isLoading: isCartLoading,
    isUpdating: isCartUpdating,
    isShippingRequired,
    cartPageData,
    deleteCartItem,
    applyPromoCode,
    removePromoCode,
    isApplyingPromoCode,
    promoCodeInputErrorMessage,
    selectedQuantities,
    handleQuantityChange,
    checkout,
    isCartEmpty,
    isError,
    didCartBecomeEmpty
  } = useCart();
  const { data: lightCart } = useGetLightCartQuery();
  const cartState = useSelector((state: RootState) => state.cart);

  const isPanelContext = props.context === 'panel';
  const isPageContext = props.context === 'page';
  const md = useMediaQuery(ScreenSizeQueries.md);
  const labels = MyCartLabels(cartPageData);
  const isXSmall = useMediaQuery(ScreenSizeQueries.xsOnly);
  const isCartBusy = isCartUpdating || cartState.isAddingToCart;
  const pageAnalyticsEvent = useRef(false);
  const panelAnalyticsEvent = useRef(false);

  const containerStyles = classnames(
    { 'flex h-full flex-col px-16': isPanelContext },
    { 'container py-5 md:pb-[90px]': isPageContext }
  );

  useEffect(() => {
    props.onBusyStateChange?.(isCartUpdating);
  }, [isCartUpdating, props]);

  const showEmptyState = isPanelContext ? didCartBecomeEmpty : isCartEmpty;

  const overlayLoading = (
    <div className="absolute inset-0 z-10 bg-transparent">
      <div
        className="absolute inset-0 animate-diagonalStripe bg-diagonal-stripe"
        style={{ backgroundSize: '200% 100%' }}
      ></div>
    </div>
  );

  const CartItems = () => {
    return cartData?.orderLines?.length === 0 && (isCartBusy || isCartLoading) ? (
      <CartItemsShimmer isPageContext={isPageContext} />
    ) : (
      <>
        {cartData?.orderLines?.map((item, index) => {
          return (
            <CartItem
              key={item.code}
              item={item}
              index={index}
              isPageContext={isPageContext}
              md={md}
              labels={labels}
              context={props.context}
              selectedQuantities={selectedQuantities}
              handleQuantityChange={handleQuantityChange}
              handleDeleteCartItem={deleteCartItem}
              handleRemovePromoCode={removePromoCode}
            />
          );
        })}
      </>
    );
  };

  const EmptyCartView = () => {
    return (
      <div>
        <h1 className={classnames('heading-5 mb-3', { 'mt-10': isPageContext })}>
          {cartPageData?.emptyCartHeader}
        </h1>
        <span className="text-1 text-gray-dark">{cartPageData?.emptyCartSubHeader}</span>
        {!!isPageContext && (
          <Button
            className="mt-10 h-14 w-52"
            href={cartPageData?.emptyCartBrowseLink?.url}
            label={cartPageData?.emptyCartBrowseButtonLabel}
            size="short"
          />
        )}
      </div>
    );
  };

  useEffect(() => {
    if (cartData && isPageContext && !pageAnalyticsEvent.current) {
      trackViewCartEvent(cartData, AnalyticsContext.CartPage);
      pageAnalyticsEvent.current = true;
    }

    if (cartData && !isPageContext && !isCartBusy && !panelAnalyticsEvent.current) {
      trackViewCartEvent(cartData, AnalyticsContext.CartPanel);
      panelAnalyticsEvent.current = true;
    }
  }, [cartData, isCartBusy, isPageContext]);

  const handleCheckoutClick = useCallback(() => {
    if (isPageContext) {
      trackSelectCheckoutEvent(AnalyticsContext.CartPage);
      checkout.buttonProps.onClick?.();
    }
  }, [checkout.buttonProps, isPageContext]);

  return (
    <div className="relative">
      {isCartBusy && overlayLoading}
      <div
        className={classnames(containerStyles, {
          'opacity-40': isCartBusy
        })}
      >
        {(!showEmptyState || isError) && (
          <h1 className="heading-4 mb-8">
            My Cart{' '}
            <span className="text-1 pt-1 align-top">({lightCart?.extendedQuantity ?? 0})</span>
          </h1>
        )}
        {isError ? (
          <SomethingWentWrongMessage
            title={cartPageData?.somethingWentWrongTitle}
            copy={cartPageData?.somethingWentWrongCopy}
            isPageContext={isPageContext}
          />
        ) : (
          <div
            className={classnames('flex flex-col-reverse gap-3', {
              'md:grid md:grid-cols-12': isPageContext
            })}
          >
            <div className="col-span-7">
              {!isCartEmpty && (
                <ContentArea // Cart Alert Content Area
                  components={cartPageData?.aboveCartContentArea}
                  propertyName="AboveCartPageContentArea"
                />
              )}
              {showEmptyState && !isCartLoading && <EmptyCartView />}
              {!showEmptyState && (
                <div>
                  <CartItems />
                </div>
              )}
              {isPageContext && ( // Cart page only content area (ex: IM Upsell banner)
                <ContentArea
                  components={cartPageData?.belowCartContentArea}
                  propertyName="BelowCartPageContentArea"
                  componentsProps={{
                    context: isPageContext ? AnalyticsContext.CartPage : AnalyticsContext.CartPanel,
                    individualMembershipInCart: cartData?.hasIndividualMembership ?? false
                  }}
                />
              )}
              <ContentArea // Cart bottom content area (content to render in both, ex: STS Renewal banner)
                components={cartPageData?.cartBottomContentArea}
                propertyName="BottomCartPageContentArea"
                componentsProps={{
                  context: isPageContext ? AnalyticsContext.CartPage : AnalyticsContext.CartPanel,
                  cartContext: props.context
                }}
              />
              {!isPageContext && (
                <ContentArea // Cart footer content area (ex: Disclaimer block), Panel variant
                  components={cartPageData?.cartFooterContentArea}
                  propertyName="CartFooterContentArea"
                  componentsProps={{ isFooterVariant: true }}
                />
              )}
            </div>

            {isPageContext && (
              <>
                <div className="col-span-1"></div>
                <div className="col-span-4">
                  <div className="flex flex-col gap-4">
                    <OrderSummaryPanel
                      subTotal={cartData?.totalWithoutDiscounts ?? 0}
                      membershipDiscountValue={cartData?.membershipDiscountValue ?? 0}
                      programsMembershipDiscountValue={
                        cartData?.programsMembershipDiscountValue ?? 0
                      }
                      publicationsMembershipDiscountValue={
                        cartData?.publicationsMembershipDiscountValue ?? 0
                      }
                      otherDiscountValue={cartData?.otherDiscountValue ?? 0}
                      shippingCost={0}
                      taxTotal={0}
                      total={cartData?.subTotal ?? 0}
                      isExpandable={isXSmall}
                      isShippingRequired={isShippingRequired}
                      isCartEmpty={isCartEmpty}
                      isShippingAddressSelected={false}
                      classNames="border border-silver"
                    />
                    {!isCartEmpty && (
                      <PromoCodeInput
                        cart={cartData}
                        context={AnalyticsContext.CartPage}
                        applyPromoCode={applyPromoCode}
                        isApplyingPromoCode={isApplyingPromoCode}
                        inputErrorMessage={promoCodeInputErrorMessage}
                      />
                    )}
                    {!isCartEmpty && (
                      <Button
                        {...checkout.buttonProps}
                        disabled={isCartBusy}
                        size="large"
                        className="hidden w-full md:flex"
                        onClick={handleCheckoutClick}
                      />
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
      {isPageContext && !isError && (
        <div className="container py-10 md:hidden">
          <Button
            {...checkout.buttonProps}
            disabled={isCartBusy}
            size="large"
            className="w-full"
            onClick={handleCheckoutClick}
          />
        </div>
      )}
      {isCartEmpty && isPageContext && !isError && (
        <ContentArea // Empty Cart footer content area (ex:Trending Block)
          components={cartPageData?.emptyCartFooterContentArea}
          propertyName="EmptyCartFooterContentArea"
          componentsProps={{
            context: AnalyticsContext.CartPage
          }}
        />
      )}
      {isPageContext && (
        <ContentArea // Cart footer content area (ex: Disclaimer block), Page variant
          components={cartPageData?.cartFooterContentArea}
          propertyName="CartFooterContentArea"
          componentsProps={{ isFooterVariant: true, nestInContainer: true }}
        />
      )}
    </div>
  );
};

export default MyCart;
